import useUpdateCustomerContact from '@/api/mutations/customers/useUpdateCustomerContacts';
import useResendEstimate from '@/api/mutations/estimates/useResendEstimate';
import useSendEstimate from '@/api/mutations/estimates/useSendEstimate';
import { useEmailSuppressionResult } from '@/api/queries/emails/useGetEmailSuppressionResults';
import useGetEstimateEmailPreview from '@/api/queries/estimates/useGetEstimateEmailPreview';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountBranding from '@/api/queries/me/useGetMyAccountBranding';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import EstimateFinancials from '@/ui/pages/estimateBuilderPage/components/EstimateFinancials';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  Field,
  Form,
  RawInput,
  SkeletonLoader,
  Textarea,
  Tooltip,
  useToastContext,
} from '@payaca/ambrosia';
import { useDebounce } from '@payaca/hooks';
import { isValidEmailAddress } from '@payaca/types/emailSchema';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm, useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { type Contact } from './contacts';
import EstimateHeaderIdentity from './EstimateHeaderIdentity';
import EstimateRecipientPicker from './EstimateRecipientPicker';

type SendMode = 'send' | 'resend';

const RECIPIENT_EMAIL_SCHEMA = z.email({
  error: 'Enter a valid email address',
});

const SEND_SCHEMA = z.object({
  intro: z.string(),
  outro: z.string(),
  email: RECIPIENT_EMAIL_SCHEMA,
});

type SendEstimateFormValues = z.infer<typeof SEND_SCHEMA>;

export type SendEstimateViewProps = {
  mode: SendMode;
  projectId: string;
  estimateId: string;
  recipient: Contact | null;
  /**
   * Contacts available for the recipient dropdown. Required to drive the
   * dropdown in send mode; ignored in resend mode (recipient is pinned).
   */
  contacts?: Contact[];
  onSelectRecipient?: (contact: Contact) => void;
  onBack?: () => void;
  onSent: () => void;
};

const getSubmitLabel = (params: {
  isResend: boolean;
  isPending: boolean;
  isBounceRecovery: boolean;
}): string => {
  const { isResend, isPending, isBounceRecovery } = params;
  if (isResend) {
    if (isPending) return 'Resending…';
    return isBounceRecovery ? 'Confirm & resend' : 'Resend';
  }
  return isPending ? 'Sending…' : 'Confirm & send';
};

const getSendDisabledReason = (params: {
  recipient: Contact | null;
}): string | null => {
  const { recipient } = params;
  if (!recipient) {
    return 'Select a Contact to send the Estimate.';
  }
  if (!recipient.email) {
    return 'The selected Contact has no email address. Add one before sending.';
  }
  if (!isValidEmailAddress(recipient.email)) {
    return `The selected Contact's email address (${recipient.email}) isn't valid. Update it on the Contact before sending.`;
  }
  return null;
};

const SendEstimateView: FC<SendEstimateViewProps> = (props) => {
  const {
    mode,
    projectId,
    estimateId,
    recipient,
    contacts = [],
    onSelectRecipient,
    onBack,
    onSent,
  } = props;

  const { data: me, isPending: isLoading } = useGetMe();
  const companyName = me?.user.account.name ?? '';

  const { pushSuccess } = useToastContext();
  const sendEstimate = useSendEstimate({ projectId });
  const resendEstimate = useResendEstimate({ projectId });
  const updateCustomerContact = useUpdateCustomerContact();
  const { estimate } = useGetProjectEstimate(projectId, estimateId);

  const isResend = mode === 'resend';
  const isBounceRecovery = isResend && !!estimate?.bouncedAt;

  const customerFirstName =
    (recipient?.name ?? '').trim().split(/\s+/)[0] || 'customer';
  const formMethods = useForm<SendEstimateFormValues>({
    defaultValues: {
      intro: `Dear ${customerFirstName},\nYour estimate can be viewed here:`,
      outro: `Thanks,\n${companyName}`,
      email: recipient?.email ?? '',
    },
    resolver: zodResolver(SEND_SCHEMA),
  });

  // Re-sync defaults when the recipient or company changes so the email body
  // references the right contact/sender. Keep dirty fields so user edits aren't
  // lost.
  useEffect(() => {
    formMethods.reset(
      {
        intro: `Dear ${customerFirstName},\nYour estimate can be viewed here:`,
        outro: `Thanks,\n${companyName}`,
        email: recipient?.email ?? '',
      },
      { keepDirtyValues: true }
    );
  }, [
    recipient?.publicId,
    recipient?.email,
    customerFirstName,
    companyName,
    formMethods,
  ]);

  const isPending =
    sendEstimate.isPending ||
    resendEstimate.isPending ||
    updateCustomerContact.isPending;

  const debouncedRecoveryEmail = useDebouncedEmail(formMethods);
  const emailToCheck = isBounceRecovery
    ? debouncedRecoveryEmail
    : recipient?.email;
  const suppression = useEmailSuppressionResult(emailToCheck);
  const isSuppressed = !!suppression?.isSuppressed;

  const finaliseResend = (values: SendEstimateFormValues) => {
    resendEstimate.mutate(
      {
        id: estimateId,
        intro: values.intro.trim() || undefined,
        outro: values.outro.trim() || undefined,
      },
      {
        onSuccess: () => {
          pushSuccess(`Estimate resent to ${recipient?.name ?? 'customer'}.`);
          onSent();
        },
      }
    );
  };

  const handleResend = (values: SendEstimateFormValues) => {
    if (!recipient) return;
    if (isBounceRecovery) {
      const trimmedEmail = values.email.trim();
      if (trimmedEmail !== (recipient.email ?? '')) {
        updateCustomerContact.mutate(
          { customerContactId: recipient.publicId, email: trimmedEmail },
          { onSuccess: () => finaliseResend(values) }
        );
        return;
      }
    }
    finaliseResend(values);
  };

  const handleSend = (values: SendEstimateFormValues) => {
    if (!recipient?.email) return;
    sendEstimate.mutate(
      {
        id: estimateId,
        customerContactId: recipient.publicId,
        intro: values.intro.trim() || undefined,
        outro: values.outro.trim() || undefined,
      },
      {
        onSuccess: () => {
          pushSuccess(`Estimate sent to ${recipient.name ?? 'customer'}.`);
          onSent();
        },
      }
    );
  };

  const handleSubmit = (values: SendEstimateFormValues) => {
    if (isPending) return;
    if (isResend) handleResend(values);
    else handleSend(values);
  };

  // In bounce-recovery the recipient email is edited inline, so its validity is
  // enforced by the visible field's schema. Otherwise (initial send and
  // non-bounce resend) the email is the pinned Contact's and can't be edited
  // here, so gate the button on it directly and surface the reason via the
  // tooltip rather than silently blocking submission on a hidden field.
  const sendDisabledReason = getSendDisabledReason({ recipient });
  const submitDisabled =
    isLoading ||
    isPending ||
    isSuppressed ||
    (isBounceRecovery ? !recipient : !!sendDisabledReason);

  const submitLabel = getSubmitLabel({ isResend, isPending, isBounceRecovery });

  return (
    <Form
      methods={formMethods}
      onSubmit={handleSubmit}
      className="flex h-full min-h-0 flex-col typography:prose"
    >
      <div className="flex items-center justify-between gap-x-8 gap-y-2 border-b border-gray-200 p-3.5 flex-wrap">
        <div className="flex items-center gap-x-2">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              colour="grey"
              size="sm"
              onClick={onBack}
              disabled={isPending}
            >
              Back to editing
            </Button>
          )}
          {isResend && (
            <EstimateHeaderIdentity
              projectId={projectId}
              estimateId={estimateId}
            />
          )}
        </div>

        <div className="flex items-center gap-x-2 ml-auto">
          <EstimateRecipientPicker
            recipient={recipient}
            contacts={contacts}
            onSelectRecipient={isResend ? undefined : onSelectRecipient}
          />

          <Tooltip
            tooltipContent={sendDisabledReason}
            enabled={!!sendDisabledReason}
          >
            <Button
              type="submit"
              variant="solid"
              size="sm"
              disabled={submitDisabled}
            >
              <UntitledIcon name="send-01.3" className="size-4" />
              {submitLabel}
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 flex flex-col overflow-y-auto">
        <EstimateFinancials projectId={projectId} estimateId={estimateId} />

        <div className="p-4">
          {isSuppressed && (
            <Alert colour="red" iconName="alert-triangle.3" className="mb-4">
              <strong>This Estimate could not be sent:</strong>
              <ul className="ml-5 list-disc">
                <li>
                  The Customer's email is blocked. Reason: {suppression?.reason}
                </li>
              </ul>
            </Alert>
          )}
          <EmailPreview
            recipient={recipient}
            estimateId={estimateId}
            editableRecipient={isBounceRecovery}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Form>
  );
};

type EmailPreviewProps = {
  recipient: Contact | null;
  estimateId: string;
  editableRecipient: boolean;
  isLoading: boolean;
};

const EmailPreview: FC<EmailPreviewProps> = (props) => {
  const { recipient, estimateId, editableRecipient, isLoading } = props;
  const { data: branding } = useGetMyAccountBranding();
  const { emailPreview, isError: isEmailPreviewError } =
    useGetEstimateEmailPreview(estimateId);

  return (
    <div className="space-y-4 typography:prose">
      <div className="flex items-center gap-x-2 font-medium text-gray-700">
        <UntitledIcon name="mail-01.3" className="size-4" />
        <span>Email Preview</span>
      </div>

      <Card>
        <Card.Body>
          <div className="-mx-4 border-b border-gray-200 px-4 pb-4 md:-mx-5 md:px-5">
            <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2">
              <span className="font-medium text-gray-500">To:</span>
              {editableRecipient ? (
                <RecipientEmailInput />
              ) : (
                <span className="text-gray-900">
                  {recipient && recipient.email
                    ? `${recipient.name ?? 'Customer'} <${recipient.email}>`
                    : (recipient?.name ?? 'Customer')}
                </span>
              )}
              {!isEmailPreviewError && (
                <>
                  <span className="font-medium text-gray-500">From:</span>
                  {emailPreview ? (
                    <span className="text-gray-900">
                      {emailPreview.fromLine}
                    </span>
                  ) : (
                    <SkeletonLoader.Text className="w-2/3" />
                  )}
                  <span className="font-medium text-gray-500">Subject:</span>
                  {emailPreview ? (
                    <span
                      aria-label="Email subject"
                      className="font-semibold text-gray-900"
                    >
                      {emailPreview.subject}
                    </span>
                  ) : (
                    <SkeletonLoader.Text className="w-3/4" />
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-4">
            {branding?.logoUrl && (
              <div className="text-center">
                <img
                  className="mx-auto h-auto max-w-[200px]"
                  src={branding.logoUrl}
                  alt="Company logo"
                />
              </div>
            )}

            {isLoading ? (
              <SkeletonLoader.Textarea />
            ) : (
              <Controller<SendEstimateFormValues, 'intro'>
                name="intro"
                render={({ field: { value, onChange, name } }) => (
                  <Field name={name}>
                    <Field.Label className="sr-only">Email intro</Field.Label>

                    <Textarea
                      value={value}
                      onChange={onChange}
                      className="text-center"
                    />
                  </Field>
                )}
              />
            )}
            <div className="text-center">
              <Button type="button" disabled>
                View Full Estimate
              </Button>
            </div>

            {isLoading ? (
              <SkeletonLoader.Textarea />
            ) : (
              <Controller<SendEstimateFormValues, 'outro'>
                name="outro"
                render={({ field: { value, onChange, name } }) => (
                  <Field name={name}>
                    <Field.Label className="sr-only">Email outro</Field.Label>

                    <Textarea
                      value={value}
                      onChange={onChange}
                      className="text-center"
                    />
                  </Field>
                )}
              />
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const useDebouncedEmail = (
  formMethods: ReturnType<typeof useForm<SendEstimateFormValues>>
): string => {
  const watchedEmail = useWatch({
    control: formMethods.control,
    name: 'email',
  });
  const [debounced, setDebounced] = useState(watchedEmail.trim());
  useDebounce(() => setDebounced(watchedEmail.trim()), 300, [watchedEmail]);
  return debounced;
};

const RecipientEmailInput: FC = () => {
  const { register, formState } = useFormContext<SendEstimateFormValues>();
  const error = formState.errors.email;
  return (
    <div className="flex flex-col gap-1">
      <RawInput
        type="text"
        inputMode="email"
        aria-label="Recipient email"
        aria-invalid={error ? 'true' : undefined}
        {...register('email')}
      />
      {error?.message && (
        <span className="text-sm text-red-600">{error.message}</span>
      )}
    </div>
  );
};

export default SendEstimateView;
